import { ChangeEvent, Dispatch, ReactNode, SetStateAction, useCallback, useMemo } from "react";
import { getDefaultParamsBySubinterface, getInputParametersBySubInterface } from "data/protocolInputParameters";
import { DeviceConfig, DeviceFamily, DeviceFamilyNames, Protocol, ProtocolId } from 'shared/models';
import { LanguageSelectField, LegacySelectField, SelectField, TextField } from "ui/shared/controls";
import { FloorOverridesField } from "./floorOverridesTable/FloorOverridesField";
import { InputParametersField } from "./inputParametersField/InputParametersField";
import { sortNames, sortNamesBy } from "logic/sortNames";
import { deepSet } from "logic/deepSet";
import { useTranslation } from "react-i18next";
import { getErrorByProtocol } from "data/protocols";

require('./BasicInfoTab.css');

export type Props = {
    config: DeviceConfig;
    readOnly?: boolean;
    deviceFamily: DeviceFamily | null;
    protocols: Record<ProtocolId, Protocol>;
    onChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
    setData: Dispatch<SetStateAction<DeviceConfig>>;
};

const displayConfigPropName = 'displayConfiguration';
const firePropNames = [ 'fireServiceMode', 'firefightersOperation', 'fireAltMode' ];

const setDisabledParameter = (paramPropName: string, config: DeviceConfig) => {
    return deepSet(config, `properties.params.${paramPropName}`, 0);
};

const deviceFamilies = Object.keys(DeviceFamily) as DeviceFamily[];


export const BasicInfoTab = ({ config, readOnly = false, deviceFamily, protocols, onChange, setData }: Props) => {
    const { t } = useTranslation();
    const isDeviceFamilyTTG = config.properties.deviceFamily === DeviceFamily.TTG;
    const isDeviceFamilyMR4 = config.properties.deviceFamily === DeviceFamily.MatisseR4;
    const selectedProtocol = protocols[config.protocolId] || null;

    const handleChangeProtocol = (protocolId: ProtocolId) => {
        const newProtocol = protocolId && protocols[protocolId];
        if (newProtocol && config.protocolId !== newProtocol.id) {
            const updatedConfig = {
                ...config,
                protocolId,
                properties: {
                    ...config.properties,
                    params: getDefaultParamsBySubinterface(newProtocol.subinterface),
                },
            } as DeviceConfig;
            setData(updatedConfig);
        }
    };

    const handleChangeParameter = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name } = e.currentTarget;
        if (name.includes(displayConfigPropName)) {
            let configFireParametersDisabled = config;
            const lipLopParamNames = getInputParametersBySubInterface(selectedProtocol.subinterface, { displayConfiguration: 1 })?.map(param => param.fieldName);
            firePropNames
                .filter(propName => lipLopParamNames?.includes(propName))
                .forEach(propName => configFireParametersDisabled = setDisabledParameter(propName, configFireParametersDisabled));
            setData(configFireParametersDisabled);
        }
        onChange(e);
    };

    const protocolOptions = useMemo(() => {
        return Object.values(protocols).sort(sortNamesBy(p => p.displayName)).map(protocol => {
            const error = getErrorByProtocol(protocol, t);
            return {
                label: protocol.displayName,
                value: protocol.id,
                isDisabled: !!error,
            };
        });
    }, [ protocols, t ]);

    const renderProtocolOption = useCallback((protocolId: ProtocolId): ReactNode => {
        const protocol = protocols[protocolId];
        const error = getErrorByProtocol(protocol, t);
        return (
            <div title={ error || undefined }>
                { error &&
                    <i className="yellow exclamation triangle icon"/>
                }
                { protocol.displayName }
            </div>
        );
    }, [ protocols, t ]);
    
    return (
        <div id="basic-info-tab">
            <LegacySelectField
                id="device-config-device-family"
                className="_family"
                labelKey="DEVICE_CONFIGS.DIALOG.FIELD.DEVICE_FAMILY"
                readOnly={ !!deviceFamily || readOnly }
                input={ {
                    name: "properties.deviceFamily",
                    value: config.properties.deviceFamily,
                    onChange,
                } }
            >
                { deviceFamilies.sort(sortNames).map(deviceFamily =>
                    <option key={ deviceFamily } value={ deviceFamily }>
                        { DeviceFamilyNames[deviceFamily] }
                    </option>
                ) }
            </LegacySelectField>

            <InputParametersField
                className="_params"
                config={ config }
                subinterface={ selectedProtocol?.subinterface || null }
                disabled={ isDeviceFamilyTTG || isDeviceFamilyMR4 }
                readOnly={ readOnly }
                onChange={ handleChangeParameter }
            />

            <SelectField
                id="device-config-protocol"
                className="_proto"
                required={ true }
                disabled={ isDeviceFamilyTTG || isDeviceFamilyMR4 }
                readOnly={ readOnly }
                labelKey="LABEL.PROTOCOL"
                name="protocolId"
                onChange={ handleChangeProtocol }
                value={ config.protocolId }
                options={ protocolOptions }
                renderOption={ renderProtocolOption }
            />

            <TextField
                id="device-config-max-persons"
                className="_persons"
                name="properties.maxPersons"
                labelKey="DEVICE_CONFIGS.DIALOG.FIELD.MAX_PERSONS"
                value={ config.properties.maxPersons }
                readOnly={ readOnly }
                onChange={ onChange }
            />

            <TextField
                id="device-config-max-weight"
                className="_weight"
                name="properties.maxWeight"
                labelKey="DEVICE_CONFIGS.DIALOG.FIELD.MAX_WEIGHT"
                value={ config.properties.maxWeight }
                readOnly={ readOnly }
                onChange={ onChange }
            />

            <LanguageSelectField
                id="device-config-lang-code"
                className="_lang"
                labelKey="LABEL.MENU_LANGUAGE"
                name="properties.langCode"
                disabled={ isDeviceFamilyTTG || isDeviceFamilyMR4 }
                value={ config.properties.langCode }
                readOnly={ readOnly }
                onChange={ onChange }
            />

            <FloorOverridesField
                className="_floors"
                config={ config }
                readOnly={ readOnly }
                setData={ setData }
                onChange={ onChange }
            />
        </div>
    );
};
